import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { SaleEvent, WithContext } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import {
  ContentDetail,
  getContentDetail,
} from '../../../lib/get-content-detail'
import { getDefaultNationalMetaData } from '../../../../../src/shared/helpers/get-default-national-metadata'
import { META_FALLBACK_IMAGE } from '../../../../../src/shared/constants'
import { shouldRedirectExpiredNationalContent } from '../../../lib/redirect-expired-content'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { NationalOfferContentDetail } from '../../../components/page-templates/content-detail/national-offer'
import { MaybeRedirect } from '@scentregroup/shared/lib/redirects'
import { shouldRedirectForNationalContentWithSlugMismatch } from '../../../lib/redirects'
import {
  fetchNationalStaticData,
  NationalStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'

export interface INationalOfferPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  nationalOffer: ContentDetail['nationalOffer']
  headerStaticData: NationalStaticData
}

function shouldRedirectWhenNotNationalOffer(
  enableNationalOffer: (string | null)[] | null,
  country: string,
  destination: string
): MaybeRedirect {
  if (!enableNationalOffer?.includes(country)) {
    return {
      redirect: {
        destination,
        permanent: false,
      },
    }
  }
  return undefined
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{ id: string; slug: string }>
): Promise<GetServerSidePropsResult<INationalOfferPageProps>> => {
  const country = localeToCountry(ctx.locale)
  const id = ctx.params?.id

  if (!id) {
    setCacheHeaders(ctx.res, 'no-cache')
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const [headerStaticData, nationalOffer, topStripProps] = await Promise.all([
    fetchNationalStaticData(apollo, { country }),
    getContentDetail(apollo, 'nationalOffer', {
      id,
    }),
    getTopStripProps(apollo, country),
  ])

  if (!nationalOffer) {
    setCacheHeaders(ctx.res, 'no-cache')
    return {
      notFound: true,
    }
  }

  const redirect =
    shouldRedirectExpiredNationalContent(nationalOffer.hideAt, '/', country) ||
    shouldRedirectWhenNotNationalOffer(
      nationalOffer.enableNationalOffer,
      country,
      '/'
    ) ||
    shouldRedirectForNationalContentWithSlugMismatch(
      'offer',
      ctx?.params?.slug,
      nationalOffer
    )

  if (redirect) {
    return redirect
  }

  setCacheHeaders(ctx.res)
  return {
    props: {
      country,
      topStripProps,
      headerStaticData,
      nationalOffer,
    },
  }
}

const NationalOfferPage: React.FC<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = props => {
  const { country, topStripProps, nationalOffer, headerStaticData } = props
  const router = useRouter()

  const metaTitle = nationalOffer.title
  const metaDescription = getDefaultNationalMetaData({
    country,
    type: 'default',
  }).description
  const metaCanonical = router.asPath
  const metaData = {
    title: metaTitle,
    description: metaDescription,
    canonical: metaCanonical,
    openGraph: {
      url: metaCanonical,
      title: metaTitle,
      description: metaDescription,
      image: nationalOffer.heroImageSet?.master?.url || META_FALLBACK_IMAGE,
    },
  }

  const ldEvent = useLDSaleEvent(nationalOffer, metaCanonical)

  return (
    <>
      <SGLinkedData item={ldEvent} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={undefined}
        header={{
          variant: 'national',
          data: headerStaticData,
        }}
        metaData={{ ...metaData, generateCanonical: true }}
        breadcrumbs={[{ label: 'Home', url: '/' }, { label: metaData.title }]}
      >
        <NationalOfferContentDetail content={nationalOffer} />
      </Layout>
    </>
  )
}

function useLDSaleEvent(
  content: ContentDetail['nationalOffer'],
  canonical: string
): WithContext<SaleEvent> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SaleEvent',
    '@id': `${canonical}#saleevent`,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    name: content.title,
    url: canonical,
    startDate: content.startAt,
    endDate: content.endAt,
    image: content.heroImageSet?.master?.url ?? undefined,
    description: content.shortDescription ?? undefined,
  }
}

export default NationalOfferPage
